import {
  Button,
  Container,
  Grid,
  IconButton,
  Stack,
  Typography,
  Alert,
  Snackbar,
  Autocomplete,
  TextField,
  Chip,
} from '@mui/material';
import { useForm, FormProvider, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router';
import { RHFTextField } from 'src/components/hook-form';
import RHFImageUpload from 'src/components/hook-form/rhf-image-upload';
import { useAuthContext } from 'src/auth/hooks';
import Iconify from 'src/components/iconify';
import { addTelecarePackage } from 'src/services/telehealth-packages.service';
import { searchTeleHealthTherapies } from 'src/services/telehealth.service';
import { getQuestionTypes } from 'src/services/questions.service';

export default function ClinicAddTherapyPackageView() {
  const { user } = useAuthContext();
  const navigate = useNavigate();

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  const [therapyOptions, setTherapyOptions] = useState([]);
  const [therapyInput, setTherapyInput] = useState('');
  const [selectedTherapies, setSelectedTherapies] = useState([]);
  const therapyDebounce = useRef(null);

  const [questionOptions, setQuestionOptions] = useState([]);
  const [selectedQuestion, setSelectedQuestion] = useState(null);

  const schema = Yup.object().shape({
    package_name: Yup.string().required(),
    starting_from: Yup.number().min(0).required(),
    visible_results_in_weeks: Yup.number().min(1).required(),
    description: Yup.string().nullable(),
    therapy_ids: Yup.array().of(Yup.string()).min(1).required(),
    promo_image: Yup.array().of(Yup.string().nullable()),
    questions_id: Yup.string().required(),
  });

  const defaultValues = {
    package_name: '',
    starting_from: null,
    visible_results_in_weeks: null,
    description: '',
    therapy_ids: [],
    promo_image: [],
    questions_id: null,
  };

  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues,
  });

  const { handleSubmit, reset, watch, setValue, control } = methods;
  const promoImages = watch('promo_image');
  const fileName = `tele_package_${new Date().toISOString()}`;
  const folderName = `images/tele-packages/${user.clinic_id}`;

  useEffect(() => {
    if (therapyDebounce.current) clearTimeout(therapyDebounce.current);

    if (therapyInput && therapyInput.length >= 2) {
      therapyDebounce.current = setTimeout(() => {
        searchTeleHealthTherapies(therapyInput).then((res) => {
          const options = (res || []).map((t) => ({ id: t.id, name: t.therapy_name }));
          setTherapyOptions(options);
        });
      }, 300);
    } else {
      setTherapyOptions([]);
    }

    return () => therapyDebounce.current && clearTimeout(therapyDebounce.current);
  }, [therapyInput]);

  useEffect(() => {
    getQuestionTypes().then((res) => {
      const options = (res || []).map((q) => ({
        id: q._id,
        label: q.type,
      }));
      setQuestionOptions(options);
    });
  }, []);

  const onSubmit = async (data) => {
    try {
      const payload = {
        ...data,
        clinic_id: user.clinic_id,
        promo_image: Array.isArray(data.promo_image) ? data.promo_image[0] : null,
      };

      await addTelecarePackage(payload);

      reset();
      setSelectedTherapies([]);
      setSelectedQuestion(null);
      setSnackbarMessage('Therapy package created successfully');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      navigate(-1);
    } catch (err) {
      setSnackbarMessage(err.message);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  return (
    <Container sx={{ background: { lg: 'white' }, margin: 4, padding: 3, maxWidth: 840, borderRadius: 4 }}>
      <Stack direction="row" gap="14px" alignItems="center" sx={{ marginBottom: '14px' }}>
        <IconButton size="large" onClick={() => navigate(-1)}>
          <Iconify icon="tabler:chevron-left" />
        </IconButton>
        <Typography variant="h4">Create Therapy Package</Typography>
      </Stack>

      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={{ xs: '14px', lg: '44px' }}>
            <Grid item xs={12} lg={6}>
              <RHFTextField name="package_name" label="Package Name" required margin="dense" />
            </Grid>

            <Grid item xs={12} lg={6}>
              <RHFTextField name="starting_from" label="Starting Price" type="number" required margin="dense" />
            </Grid>

            <Grid item xs={12} lg={6}>
              <RHFTextField
                name="visible_results_in_weeks"
                label="Visible Results (Weeks)"
                type="number"
                required
                margin="dense"
              />
            </Grid>

            <Grid item xs={12} lg={6}>
              <Controller
                name="questions_id"
                control={control}
                render={({ field }) => (
                  <Autocomplete
                    options={questionOptions}
                    getOptionLabel={(option) => option.label}
                    value={selectedQuestion}
                    onChange={(_, value) => {
                      setSelectedQuestion(value);
                      field.onChange(value?.id || null);
                    }}
                    renderInput={(params) => (
                      <TextField {...params} label="Assessment Questions Type" margin="dense" required />
                    )}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="therapy_ids"
                control={control}
                render={({ field }) => (
                  <Autocomplete
                    multiple
                    options={therapyOptions}
                    getOptionLabel={(option) => option.name}
                    inputValue={therapyInput}
                    onInputChange={(_, value) => setTherapyInput(value)}
                    value={selectedTherapies}
                    onChange={(_, value) => {
                      setSelectedTherapies(value);
                      field.onChange(value.map((v) => v.id));
                    }}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip label={option.name} {...getTagProps({ index })} key={option.id} />
                      ))
                    }
                    renderInput={(params) => <TextField {...params} label="Search Therapies" margin="dense" />}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <RHFTextField name="description" label="Description" multiline rows={4} margin="dense" />
            </Grid>

            <Grid item xs={12}>
              <RHFImageUpload
                initialImage={promoImages}
                fileName={fileName}
                setValue={setValue}
                name="promo_image"
                folderName={folderName}
                displayImages
                maxFiles={1}
                label="Promo Image"
                width={300}
                height={200}
              />
            </Grid>

            <Grid item xs={12}>
              <Button
                type="submit"
                variant="contained"
                fullWidth
                sx={{
                  boxShadow: 10,
                  fontSize: { lg: '16px', xs: '12px' },
                  background: '#D92D7F',
                  ':hover': { background: '#D92D7F' },
                }}
              >
                Save Therapy Package
              </Button>
            </Grid>
          </Grid>
        </form>
      </FormProvider>

      <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={() => setSnackbarOpen(false)}>
        <Alert severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
}
